import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Usuario } from '@copathy/shared';
import { ErrorApi } from '../lib/api';
import { useGuardarUsuario, useUsuarios, type FiltrosUsuarios } from '../lib/usuarios';
import { useSolicitudes } from '../lib/solicitudes';
import { useClientes } from '../lib/clientes';
import { useMe, usePuede } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { Boton } from '../componentes/Boton';
import { BuscadorFiltros, type SeccionFiltro } from '../componentes/BuscadorFiltros';
import { Combo } from '../componentes/Combo';
import { Pestanas } from '../componentes/Pestanas';
import { FormularioUsuario } from '../componentes/FormularioUsuario';
import { EnlaceDeInvitacion } from '../componentes/EnlaceDeInvitacion';
import { PanelSolicitudes } from './Solicitudes';
import { Check, Personas, Reloj } from '../componentes/iconos';

/**
 * M1/M3 — Usuarios.
 *
 * ── Tres pestañas, y por qué esas ──
 * · **Personal de Copathy** — unos pocos que administran la plataforma.
 * · **Usuarios de clientes** — muchos, llegan solos por autorregistro y se agrupan
 *   por cliente. Acá conviven el administrador del cliente y el trabajador: roles
 *   distintos pero el mismo mundo, y separarlos serían cuatro pestañas para lo que
 *   el filtro de rol ya resuelve.
 * · **Solicitudes** — una solicitud **es una cuenta que todavía no existe**.
 *   Tenerla en otra sección obligaba a recordar dónde mirar (D-045).
 *
 * El ámbito no es un campo nuevo: se deriva de `roles.ambito`, el mismo dato con
 * el que el motor sostiene la frontera entre empresas.
 *
 * ── Por qué la fila ya no tiene botones ──
 * Tenía «Editar» y «Desactivar» en cada fila, y «Reenviar invitación» solo en
 * algunas. Como ese bloque medía distinto en cada renglón, **el espacio sobrante
 * se repartía distinto y las columnas no alineaban**: la de cliente arrancaba en
 * una x diferente en cada fila. Ahora es una rejilla de columnas fijas, la fila
 * lleva al perfil, y las acciones viven ahí.
 */
export const ETIQUETA_ESTADO: Record<string, { texto: string; clases: string }> = {
  activo: { texto: 'Activa', clases: 'bg-green-50 text-green-800' },
  sin_activar: { texto: 'Sin activar', clases: 'bg-amber-50 text-amber-800' },
  /*
    «Archivada» es el rótulo de `desactivado`. Un solo concepto, no dos estados
    que significan lo mismo: dos estados equivalentes divergen solos, y el día que
    uno de los dos deje de comprobarse en algún sitio, alguien archivado sigue
    entrando.
  */
  desactivado: { texto: 'Archivada', clases: 'bg-marino-50 text-marino-500' },
};

export const fecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });

type Pestana = 'copathy' | 'clientes' | 'solicitudes';

/**
 * Las columnas, en un solo sitio.
 *
 * ⚠️ El encabezado y las filas usan **la misma cadena**. Con dos definiciones,
 * agregar una columna arriba y olvidarla abajo desalinea todo — que es
 * exactamente el defecto que esta pantalla tenía.
 */
const REJILLA = 'grid grid-cols-[1.25rem_minmax(0,2fr)_minmax(0,1.4fr)_9rem_8rem] gap-x-4 px-4';

export function Usuarios() {
  const [pestana, setPestana] = useState<Pestana>('copathy');
  const [texto, setTexto] = useState('');
  const [filtros, setFiltros] = useState<FiltrosUsuarios>({});
  const [editando, setEditando] = useState<Usuario | 'nuevo' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enlaces, setEnlaces] = useState<{ nombre: string; enlace: string }[]>([]);
  const [marcados, setMarcados] = useState<number[]>([]);

  useMigas([{ texto: 'Usuarios' }]);
  const ir = useNavigate();

  const { usuario: yo } = useMe();
  const puede = usePuede('usuarios.administrar');
  const puedeVerSolicitudes = usePuede('solicitudes.ver');

  const esClientes = pestana === 'clientes';
  const consulta = useUsuarios(texto, {
    ...filtros,
    ambito: esClientes ? 'cliente' : 'copathy',
  });
  const { reinvitarVarias } = useGuardarUsuario();
  const clientes = useClientes('', { activo: 'si' });

  // El contador va SIEMPRE, aunque la pestaña esté cerrada: es lo que hace que
  // alguien entre a revisar la cola sin que se lo recuerden.
  const pendientes = useSolicitudes('pendiente');

  const secciones: SeccionFiltro[] = useMemo(
    () => [
      {
        titulo: 'Rol y estado',
        campos: [
          {
            tipo: 'multi',
            clave: 'rol',
            texto: 'Rol',
            // Los roles de una pestaña no existen en la otra: ofrecerlos todos
            // sería dejar elegir filtros que siempre dan cero.
            opciones: esClientes
              ? [
                  { valor: 'admin_empresa', texto: 'Administrador del cliente' },
                  { valor: 'trabajador', texto: 'Trabajador' },
                ]
              : [
                  { valor: 'admin_copathy', texto: 'Administrador Copathy' },
                  { valor: 'consultor_copathy', texto: 'Consultor Copathy' },
                ],
          },
          {
            tipo: 'multi',
            clave: 'estado',
            texto: 'Estado',
            opciones: [
              { valor: 'activo', texto: 'Activas' },
              { valor: 'sin_activar', texto: 'Sin activar' },
              { valor: 'desactivado', texto: 'Archivadas' },
            ],
          },
          { tipo: 'booleano', clave: 'invitacionPendiente', texto: 'Con invitación sin usar' },
        ],
      },
      {
        titulo: 'Fechas',
        campos: [
          {
            tipo: 'fecha',
            claveDesde: 'registroDesde',
            claveHasta: 'registroHasta',
            texto: 'Registro',
          },
          {
            tipo: 'fecha',
            claveDesde: 'accesoDesde',
            claveHasta: 'accesoHasta',
            texto: 'Último acceso',
          },
        ],
      },
    ],
    [esClientes],
  );

  const lista = consulta.data?.datos ?? [];
  const invitables = lista.filter((u) => u.estado === 'sin_activar').map((u) => u.id);
  // Solo cuentan las marcadas que siguen a la vista: si alguien marca, filtra y
  // reenvía, mandaría correos a gente que ya no está mirando.
  const marcadosVisibles = marcados.filter((id) => invitables.includes(id));

  async function reenviarMarcadas() {
    setError(null);
    try {
      const r = await reinvitarVarias.mutateAsync(marcadosVisibles);
      setMarcados([]);
      setEnlaces(r.enlacesDev ?? []);
      /*
        Las fallidas se dicen SIEMPRE, con nombre y motivo. Un reenvío que responde
        «listo» y dejó tres afuera es peor que uno que falla entero: nadie va a ir
        a revisar cuáles, y esas personas siguen esperando un correo.
      */
      if (r.fallidas.length > 0) {
        setError(
          `Se reenviaron ${r.enviadas}. No se pudo con: ${r.fallidas
            .map((f) => `${f.nombre} (${f.motivo})`)
            .join(' · ')}`,
        );
      }
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos reenviar las invitaciones.');
    }
  }

  const cambiarPestana = (p: Pestana) => {
    setPestana(p);
    // Los filtros no cruzan de pestaña: los roles de una no existen en la otra, y
    // arrastrarlos deja una lista vacía sin explicar por qué.
    setFiltros({});
    setMarcados([]);
  };

  const PESTANAS: { clave: Pestana; texto: string; n?: number; visible: boolean }[] = [
    { clave: 'copathy', texto: 'Personal de Copathy', visible: puede },
    { clave: 'clientes', texto: 'Usuarios de clientes', visible: puede },
    {
      clave: 'solicitudes',
      texto: 'Solicitudes',
      n: pendientes.data?.total,
      visible: puedeVerSolicitudes,
    },
  ];

  return (
    <Contenedor>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-marino-800">Usuarios</h1>
        <p className="mt-1 text-sm text-marino-400">
          Quién entra a la plataforma: el personal de Copathy, la gente de cada cliente, y quien
          está esperando acceso.
        </p>
      </div>

      {pestana !== 'solicitudes' && (
        <div className="mb-4">
          <BuscadorFiltros
            busqueda={texto}
            alBuscar={setTexto}
            guia={esClientes ? 'Buscar por nombre, correo o cliente' : 'Buscar por nombre o correo'}
            secciones={secciones}
            valores={filtros}
            alAplicar={setFiltros}
            derecha={
              puede ? (
                <Boton onClick={() => setEditando('nuevo')} className="py-3">
                  Crear cuenta
                </Boton>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Las pestañas van DEBAJO del buscador y encima de la tabla: el buscador
          manda sobre la pestaña activa, no al revés. Mismo componente que en
          Clientes y Cuestionarios — una barra de pestañas por pantalla es cómo
          terminan siendo tres que se ven distinto. */}
      <Pestanas
        etiqueta="Tipo de usuario"
        pestanas={PESTANAS.filter((p) => p.visible).map(({ clave, texto, n }) => ({
          clave,
          texto,
          n,
        }))}
        activa={pestana}
        alElegir={cambiarPestana}
      />

      {pestana === 'solicitudes' ? (
        <PanelSolicitudes />
      ) : (
        <>
          {/* Rápidos: el cliente y las archivadas son los dos que se usan a diario,
              y meterlos dentro del panel costaría dos clics cada vez. */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            {esClientes && (
              <div className="min-w-64">
                <Combo
                  etiqueta="Cliente"
                  opciones={(clientes.data?.datos ?? []).map((c) => ({
                    clave: String(c.id),
                    texto: c.nombre,
                  }))}
                  valor={filtros.empresaId ?? null}
                  alCambiar={(v) =>
                    setFiltros((f) => ({ ...f, empresaId: v === null ? undefined : String(v) }))
                  }
                  vacio="Todos los clientes"
                  sinCoincidencias="Ningún cliente coincide."
                />
              </div>
            )}

            <button
              type="button"
              onClick={() =>
                setFiltros((f) => ({
                  ...f,
                  estado: f.estado === 'desactivado' ? undefined : 'desactivado',
                }))
              }
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                filtros.estado === 'desactivado'
                  ? 'bg-marino-800 text-white'
                  : 'bg-white text-marino-500 ring-1 ring-marino-200 hover:text-marino-800'
              }`}
            >
              Archivadas
            </button>

            <span className="ml-auto text-sm text-marino-400">
              {consulta.data ? `${consulta.data.total} en total` : ''}
            </span>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {error}
            </div>
          )}

          {enlaces.length > 0 && (
            <div className="mb-4 space-y-2">
              {enlaces.map((e) => (
                <EnlaceDeInvitacion
                  key={e.enlace}
                  enlace={e.enlace}
                  nombre={e.nombre}
                  alCerrar={() => setEnlaces((l) => l.filter((x) => x.enlace !== e.enlace))}
                />
              ))}
            </div>
          )}

          {/* Acción masiva: reemplaza el botón por fila. Aparece solo cuando hay
              algo marcado, para no ocupar altura el resto del tiempo. */}
          {marcadosVisibles.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-naranja-200 bg-naranja-50 px-4 py-3">
              <span className="text-sm font-semibold text-naranja-900">
                {marcadosVisibles.length}{' '}
                {marcadosVisibles.length === 1 ? 'cuenta marcada' : 'cuentas marcadas'}
              </span>
              <Boton
                className="px-3.5 py-2 text-sm"
                cargando={reinvitarVarias.isPending}
                onClick={() => void reenviarMarcadas()}
              >
                Reenviar invitación
              </Boton>
              <button
                type="button"
                onClick={() => setMarcados([])}
                className="text-sm font-semibold text-naranja-800 hover:underline"
              >
                Quitar la selección
              </button>
            </div>
          )}

          {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}
          {consulta.isError && (
            <p className="py-16 text-center text-red-600">
              No pudimos cargar los usuarios. Recarga la página.
            </p>
          )}

          {consulta.data && lista.length === 0 && (
            <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
                <Personas size={24} />
              </div>
              <h2 className="font-semibold text-marino-700">Ninguna cuenta coincide</h2>
              <p className="mt-1 text-sm text-marino-400">
                Prueba con otro texto, o quita algún filtro.
              </p>
            </div>
          )}

          {lista.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-marino-100 bg-white">
              <div
                className={`${REJILLA} border-b border-marino-100 bg-marino-50/50 py-2.5 text-xs font-semibold uppercase tracking-wide text-marino-400`}
              >
                <span aria-hidden="true" />
                <span>Persona</span>
                <span>{esClientes ? 'Cliente' : 'Rol'}</span>
                <span>Estado</span>
                <span>Último acceso</span>
              </div>

              <ul className="divide-y divide-marino-100">
                {lista.map((u) => (
                  <li key={u.id}>
                    <div className={`${REJILLA} items-center py-3`}>
                      {/*
                        La casilla va FUERA del enlace al perfil: anidar un control
                        dentro de otro accionable haría que marcar navegue.
                      */}
                      {u.estado === 'sin_activar' ? (
                        <button
                          type="button"
                          aria-label={`Marcar a ${u.nombre} para reenviarle la invitación`}
                          onClick={() =>
                            setMarcados((m) =>
                              m.includes(u.id) ? m.filter((x) => x !== u.id) : [...m, u.id],
                            )
                          }
                          className={`grid h-4 w-4 place-items-center rounded border transition ${
                            marcados.includes(u.id)
                              ? 'border-naranja-500 bg-naranja-500 text-marino-900'
                              : 'border-marino-300 hover:border-marino-500'
                          }`}
                        >
                          {marcados.includes(u.id) && <Check size={11} />}
                        </button>
                      ) : (
                        <span aria-hidden="true" />
                      )}

                      <button
                        type="button"
                        onClick={() => ir(`/usuarios/${u.id}`)}
                        className="min-w-0 text-left transition hover:text-naranja-700
                          focus:outline-none focus-visible:underline"
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-semibold text-marino-800">{u.nombre}</span>
                          {u.id === yo?.id && (
                            <span className="rounded-full bg-marino-50 px-2 py-0.5 text-xs font-semibold text-marino-500">
                              Tú
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-sm text-marino-400">{u.correo}</span>
                      </button>

                      <span className="min-w-0 text-sm">
                        <span className="block truncate text-marino-700">
                          {esClientes ? u.empresaNombre : u.rolNombre}
                        </span>
                        <span className="block truncate text-marino-400">
                          {esClientes ? u.rolNombre : `Desde ${fecha(u.creadoEn)}`}
                        </span>
                      </span>

                      <span className="flex flex-col items-start gap-1">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            ETIQUETA_ESTADO[u.estado]?.clases ?? 'bg-marino-50 text-marino-500'
                          }`}
                        >
                          {ETIQUETA_ESTADO[u.estado]?.texto ?? u.estado}
                        </span>
                        {u.invitacionPendiente && (
                          <span className="flex items-center gap-1 text-xs text-marino-400">
                            <Reloj size={12} /> Invitada
                          </span>
                        )}
                      </span>

                      <span className="text-sm text-marino-400">
                        {u.ultimoAccesoEn ? fecha(u.ultimoAccesoEn) : 'Nunca entró'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <FormularioUsuario
        objetivo={editando}
        copathyId={yo?.empresaId ?? null}
        alCerrar={() => setEditando(null)}
        alFallar={setError}
        alInvitar={(e, nombre) =>
          e && setEnlaces([{ nombre: nombre ?? 'la cuenta nueva', enlace: e }])
        }
      />
    </Contenedor>
  );
}
